import React, { useEffect, useState } from "react";
import { api, fileUrl, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import ProgressGauge from "../components/ProgressGauge";
import { toast } from "sonner";
import { Pencil, Award, Gift, Truck } from "lucide-react";
import { WhatsAppButton } from "../components/WhatsApp";
import { Link } from "react-router-dom";

const SPORTS = ["Football", "Basketball", "Rugby", "Handball", "Volleyball", "Tennis", "Athlétisme", "Hockey"];
const LEVELS = ["Loisir", "Départemental", "Régional", "National"];
const AGE_CATS = ["U7", "U9", "U11", "U13", "U15", "U17", "U18", "Senior", "Vétéran"];
const COLORS = ["Bleu", "Blanc", "Rouge", "Noir", "Vert", "Jaune", "Orange", "Violet", "Rose", "Gris"];

export default function DashboardClub() {
    const { user } = useAuth();
    const [club, setClub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sponsorships, setSponsorships] = useState([]);
    const [jerseys, setJerseys] = useState([]);
    const [editing, setEditing] = useState(false);
    const [loyalty, setLoyalty] = useState(null);

    const reload = async () => {
        const { data } = await api.get("/sponsorships/club");
        setSponsorships(data.items || []);
        if (data.club) {
            setClub(data.club);
            const { data: cd } = await api.get(`/clubs/${data.club.id}`);
            setJerseys(cd.jerseys || []);
            try {
                const { data: ld } = await api.get("/clubs/loyalty");
                setLoyalty(ld);
            } catch {
                // best-effort fetch, ignore errors
            }
        } else {
            setClub(null);
        }
        setLoading(false);
    };

    useEffect(() => {
        reload().catch(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-12 text-zinc-500">Chargement…</div>;

    if (!club || editing) {
        return (
            <ClubProfileForm
                user={user}
                initial={club}
                onDone={() => {
                    setEditing(false);
                    reload();
                }}
            />
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-10">
            {club.status !== "approved" && (
                <div
                    data-testid="club-status-banner"
                    className={[
                        "card-tu border-l-4",
                        club.status === "pending" ? "border-l-orange-500" : "border-l-red-600",
                    ].join(" ")}
                >
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold mb-1 text-orange-500">
                        Statut : {club.status}
                    </div>
                    {club.status === "pending" ? (
                        <p className="text-zinc-300">
                            Votre fiche est <span className="font-bold">en attente de modération</span>.
                            Un administrateur va valider votre club avant publication. Vous pourrez ensuite recevoir des sponsors.
                        </p>
                    ) : (
                        <p className="text-zinc-300">
                            Votre fiche a été <span className="font-bold">refusée</span>. Modifiez-la et contactez le support si besoin.
                        </p>
                    )}
                </div>
            )}
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                        Espace club
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-none">
                        {club.name}
                    </h1>
                    <p className="text-zinc-400 mt-2">{club.city} · {club.sport} · {club.num_players} joueurs</p>
                    {(club.president_name || club.president_phone) && (
                        <p className="text-xs text-zinc-500 mt-1" data-testid="club-president-info">
                            Président : <span className="text-zinc-300 font-bold">{club.president_name || "—"}</span>
                            {club.president_phone && (
                                <> · <a className="text-orange-500 font-mono" href={`tel:${club.president_phone}`}>{club.president_phone}</a></>
                            )}
                        </p>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        data-testid="edit-club-btn"
                        onClick={() => setEditing(true)}
                        className="btn-ghost py-2 px-4 text-xs"
                    >
                        <Pencil size={14} /> Modifier
                    </button>
                    <Link
                        to={`/clubs/${club.id}`}
                        className="btn-primary py-2 px-4 text-xs"
                    >
                        Voir ma fiche publique
                    </Link>
                </div>
            </header>

            {/* JERSEYS */}
            <section>
                <h2 className="font-display text-3xl uppercase tracking-tight mb-6">Mes maillots</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {jerseys.map((j) => (
                        <JerseyCard key={j.id} jersey={j} />
                    ))}
                </div>
            </section>

            {/* CLUB LOYALTY */}
            {loyalty && <ClubLoyaltyCard loyalty={loyalty} onAddressSaved={reload} reload={reload} />}

            {/* SPONSORS RECEIVED */}
            <section>
                <h2 className="font-display text-3xl uppercase tracking-tight mb-6">
                    Sponsors reçus
                </h2>
                {sponsorships.length === 0 ? (
                    <div className="card-tu text-zinc-500 text-center py-10">
                        Aucun sponsor pour l'instant.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {sponsorships.map((s) => (
                            <SponsorshipRow key={s.id} sp={s} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function ClubLoyaltyCard({ loyalty, onAddressSaved, reload }) {
    const next = loyalty.next_reward;
    const nextCost = next ? Number(next.points_cost ?? next.points ?? 0) : 0;
    const cheapest = loyalty.cheapest_cost ?? nextCost;
    const progress = nextCost
        ? Math.min(100, Math.round((loyalty.points / nextCost) * 100))
        : 100;
    const catalog = loyalty.catalog || [];
    const claimsHistory = Array.isArray(loyalty.claims) ? loyalty.claims : [];
    const hasShippableClaim = claimsHistory.some((c) => ["pending", "preparing"].includes(c.status));
    const [editAddr, setEditAddr] = React.useState(false);
    const [addr, setAddr] = React.useState("");
    const [saving, setSaving] = React.useState(false);
    const [claimingId, setClaimingId] = React.useState(null);

    const STATUS_LABEL = {
        pending: { cls: "border-orange-500/40 text-orange-500", l: "À préparer" },
        preparing: { cls: "border-yellow-700 text-yellow-400", l: "En préparation" },
        shipped: { cls: "border-cyan-700 text-cyan-400", l: "Expédiée" },
        delivered: { cls: "border-green-700 text-green-400", l: "Livrée" },
        cancelled: { cls: "border-red-900 text-red-400", l: "Annulée" },
    };

    const saveAddress = async () => {
        if (!addr.trim()) {
            toast.error("Saisissez votre adresse");
            return;
        }
        setSaving(true);
        try {
            await api.post("/clubs/loyalty/address", { shipping_address: addr });
            toast.success("Adresse enregistrée");
            setEditAddr(false);
            onAddressSaved?.();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const claim = async (rewardId, cost, name) => {
        if (!window.confirm(`Échanger ${cost} points contre "${name}" ?`)) return;
        setClaimingId(rewardId);
        try {
            const { data } = await api.post("/clubs/loyalty/claim", { reward_id: rewardId });
            toast.success(`Récompense réservée ! Solde : ${data.remaining_points} pts`);
            reload?.();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setClaimingId(null);
        }
    };

    return (
        <section className="card-tu" data-testid="club-loyalty-card">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-3">
                    <Award className="text-orange-500" size={24} />
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold">
                            Boutique fidélité club
                        </div>
                        <div className="font-display text-3xl uppercase tracking-tight">
                            <span data-testid="club-loyalty-points" className="text-orange-500">{loyalty.points}</span> points
                        </div>
                    </div>
                </div>
                {next && (
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                            Prochaine récompense accessible
                        </div>
                        <div className="font-display text-2xl">
                            {Math.max(0, nextCost - loyalty.points)}<span className="text-zinc-500 text-base"> pts</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="w-full h-2 bg-zinc-900 border border-zinc-800 overflow-hidden mb-6">
                <div className="neon-bar" style={{ width: `${progress}%` }} />
            </div>

            {/* Shipping address — visible once the club has at least one claim to ship */}
            {hasShippableClaim && (
                <div className="mb-6 border border-zinc-800 bg-zinc-950 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 font-bold">
                            <Truck size={12} className="inline mr-1 -mt-0.5 text-orange-500" />
                            Adresse de livraison des récompenses
                        </div>
                        {!editAddr && (
                            <button
                                onClick={() => {
                                    const current = claimsHistory.find((c) => c.shipping_address)?.shipping_address || "";
                                    setAddr(current);
                                    setEditAddr(true);
                                }}
                                className="text-xs uppercase tracking-widest text-orange-500"
                                data-testid="edit-shipping-address"
                            >
                                Modifier
                            </button>
                        )}
                    </div>
                    {editAddr ? (
                        <div className="space-y-2">
                            <textarea
                                rows={2}
                                value={addr}
                                onChange={(e) => setAddr(e.target.value)}
                                placeholder="Adresse complète"
                                className="input-tu"
                                data-testid="shipping-address-input"
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={saveAddress}
                                    disabled={saving}
                                    data-testid="save-shipping-address"
                                    className="btn-primary py-2 px-4 text-xs"
                                >
                                    Enregistrer
                                </button>
                                <button onClick={() => setEditAddr(false)} className="btn-ghost py-2 px-4 text-xs">
                                    Annuler
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-sm text-zinc-300">
                            {claimsHistory.find((c) => c.shipping_address)?.shipping_address || (
                                <span className="text-zinc-500 italic">
                                    Renseignez votre adresse pour recevoir vos récompenses.
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* CATALOGUE BOUTIQUE */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className="font-display text-2xl uppercase tracking-tight">Catalogue fidélité</h3>
                <WhatsAppButton testid="catalogue-whatsapp-cta" />
            </div>
            <p className="text-xs text-zinc-400 mb-4">
                Contactez-nous via WhatsApp pour nous donner les informations pour créer le design.
            </p>
            {catalog.length === 0 ? (
                <div className="text-zinc-500 text-sm italic mb-6">Aucune récompense disponible pour le moment.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                    {catalog.map((r) => {
                        const affordable = loyalty.points >= r.points_cost;
                        return (
                            <div
                                key={r.id}
                                data-testid={`club-reward-${r.id}`}
                                className={[
                                    "border p-4 flex flex-col gap-3",
                                    affordable ? "border-orange-500/60 bg-orange-500/5" : "border-zinc-800 bg-zinc-950",
                                ].join(" ")}
                            >
                                <div className="flex items-start gap-3">
                                    {r.image_path ? (
                                        <img
                                            src={fileUrl(r.image_path)}
                                            alt={r.name}
                                            className="w-16 h-16 object-cover border border-zinc-800 shrink-0"
                                        />
                                    ) : (
                                        <div className="w-16 h-16 flex items-center justify-center border border-zinc-800 shrink-0">
                                            <Gift size={24} className={affordable ? "text-orange-500" : "text-zinc-600"} />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                                            {r.points_cost} points
                                        </div>
                                        <div className={[
                                            "font-display text-xl uppercase tracking-tight truncate",
                                            affordable ? "text-orange-500" : "text-zinc-300",
                                        ].join(" ")}>
                                            {r.name}
                                        </div>
                                        {r.description && (
                                            <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{r.description}</div>
                                        )}
                                    </div>
                                </div>
                                <button
                                    disabled={!affordable || claimingId === r.id}
                                    onClick={() => claim(r.id, r.points_cost, r.name)}
                                    data-testid={`club-reward-claim-${r.id}`}
                                    className={[
                                        "py-2 px-3 text-xs uppercase tracking-widest font-bold transition-colors",
                                        affordable
                                            ? "bg-gradient-to-r from-orange-600 to-red-600 text-white hover:opacity-90"
                                            : "border border-zinc-800 text-zinc-600 cursor-not-allowed",
                                    ].join(" ")}
                                >
                                    {claimingId === r.id ? "…" : affordable ? "Échanger" : `Manque ${r.points_cost - loyalty.points} pts`}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* HISTORIQUE */}
            {claimsHistory.length > 0 && (
                <>
                    <h3 className="font-display text-2xl uppercase tracking-tight mb-3">Historique</h3>
                    <div className="space-y-2">
                        {claimsHistory.map((cl) => {
                            const st = STATUS_LABEL[cl.status] || { cls: "border-zinc-700 text-zinc-300", l: cl.status };
                            return (
                                <div
                                    key={cl.id}
                                    data-testid={`reward-claim-${cl.id}`}
                                    className="border border-zinc-800 bg-zinc-950 p-3 flex flex-wrap items-center gap-3"
                                >
                                    <div className="flex-1 min-w-[200px]">
                                        <div className="font-bold text-sm">{cl.reward_label}</div>
                                        <div className="text-[11px] text-zinc-500">
                                            {(cl.points_cost ?? cl.threshold)} pts · {(cl.created_at || "").slice(0, 10)}
                                        </div>
                                        {cl.tracking_number && (
                                            <div className="text-[11px] text-zinc-400 font-mono mt-0.5">
                                                Suivi : <span className="text-orange-500">{cl.tracking_number}</span>
                                            </div>
                                        )}
                                    </div>
                                    <span className={`chip ${st.cls}`} data-testid={`reward-status-${cl.id}`}>
                                        {st.l}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </section>
    );
}

function JerseyCard({ jersey }) {
    const ready = jersey.total_collected >= 19;
    const statusLabel = {
        collecting: "Collecte en cours",
        ready: "Prêt à valider",
        validated: "Validé",
        in_production: "En production",
        delivered: "Livré",
    }[jersey.status] || jersey.status;

    return (
        <div className="card-tu" data-testid={`jersey-card-${jersey.age_category}`}>
            <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-3xl uppercase tracking-tight">
                    {jersey.age_category}
                </h3>
                <span className={[
                    "chip",
                    jersey.status === "delivered" ? "border-green-700 text-green-400" :
                    jersey.status === "in_production" || jersey.status === "validated" ? "border-orange-500/40 text-orange-500" :
                    ready ? "border-orange-500/40 text-orange-500" : "border-zinc-700 text-zinc-300"
                ].join(" ")}>
                    {statusLabel}
                </span>
            </div>
            <ProgressGauge value={jersey.total_collected} max={19} hideAmount />
            {ready && jersey.status === "ready" && (
                <div className="mt-4 px-3 py-2 border border-orange-500/40 bg-orange-500/10 text-[11px] uppercase tracking-widest text-orange-500 text-center">
                    Seuil atteint — la production sera lancée par l'administration.
                </div>
            )}
        </div>
    );
}

function SponsorshipRow({ sp }) {
    const statusBadge = {
        pending: { cls: "border-zinc-700 text-zinc-300", l: "En attente" },
        club_approved: { cls: "border-green-800 text-green-400", l: "Actif" },
        club_refused: { cls: "border-red-900 text-red-400", l: "Refusé" },
        in_production: { cls: "border-orange-500/40 text-orange-500", l: "En production" },
        delivered: { cls: "border-green-800 text-green-400", l: "Livré" },
    }[sp.status] || { cls: "border-zinc-700 text-zinc-300", l: sp.status };
    return (
        <div className="card-tu flex flex-wrap items-center gap-4">
            <div className="bg-zinc-950 border border-zinc-800 w-20 h-20 flex items-center justify-center shrink-0">
                <img src={fileUrl(sp.logo_path)} alt="logo" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="flex-1 min-w-[200px]">
                <div className="font-bold">{sp.sponsor_company}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-widest">
                    {sp.age_category} · {sp.zones?.map((z) => z.name).join(", ")} · {sp.num_players} maillots
                </div>
                {sp.message && <div className="text-sm text-zinc-300 mt-1 italic">"{sp.message}"</div>}
            </div>
            <span className={`chip ${statusBadge.cls}`}>{statusBadge.l}</span>
        </div>
    );
}

function ClubProfileForm({ user, initial, onDone }) {
    const [f, setF] = useState({
        name: initial?.name || user?.name || "",
        sport: initial?.sport || "Football",
        department: initial?.department || "",
        city: initial?.city || "",
        teams_by_category: initial?.teams_by_category || [{ age_category: "Senior", num_teams: 1, players_per_team: 12 }],
        level: initial?.level || "Loisir",
        colors: initial?.colors || ["Bleu"],
        needs: initial?.needs || "",
        description: initial?.description || "",
        president_name: initial?.president_name || "",
        president_phone: initial?.president_phone || "",
    });
    const [saving, setSaving] = useState(false);

    const toggleColor = (v) => {
        setF((x) => ({
            ...x,
            colors: x.colors.includes(v) ? x.colors.filter((y) => y !== v) : [...x.colors, v],
        }));
    };

    const toggleAgeCategory = (cat) => {
        setF((x) => {
            const exists = x.teams_by_category.find((t) => t.age_category === cat);
            return {
                ...x,
                teams_by_category: exists
                    ? x.teams_by_category.filter((t) => t.age_category !== cat)
                    : [...x.teams_by_category, { age_category: cat, num_teams: 1, players_per_team: 12 }],
            };
        });
    };

    const updateTeam = (cat, field, value) => {
        setF((x) => ({
            ...x,
            teams_by_category: x.teams_by_category.map((t) =>
                t.age_category === cat ? { ...t, [field]: Math.max(0, +value || 0) } : t
            ),
        }));
    };

    const totalPlayers = f.teams_by_category.reduce(
        (s, t) => s + (t.num_teams || 0) * (t.players_per_team || 0), 0
    );
    const totalTeams = f.teams_by_category.reduce((s, t) => s + (t.num_teams || 0), 0);

    const save = async (e) => {
        e.preventDefault();
        if (f.teams_by_category.length === 0) {
            toast.error("Sélectionnez au moins une catégorie d'âge.");
            return;
        }
        const invalid = f.teams_by_category.find((t) => !t.num_teams || !t.players_per_team);
        if (invalid) {
            toast.error(`Renseignez équipes et joueurs pour ${invalid.age_category}.`);
            return;
        }
        if (!f.president_name.trim()) {
            toast.error("Le nom du président est requis.");
            return;
        }
        const presPhone = f.president_phone.replace(/[\s.\-]/g, "");
        if (!/^0\d{9}$/.test(presPhone)) {
            toast.error("Téléphone du président invalide (10 chiffres, ex. 06 12 34 56 78).");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...f, age_categories: f.teams_by_category.map((t) => t.age_category) };
            if (initial) await api.put(`/clubs/${initial.id}`, payload);
            else await api.post("/clubs", payload);
            toast.success(initial ? "Club mis à jour" : "Club créé — en attente de modération");
            onDone();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-6 py-12">
            <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                {initial ? "Modifier" : "Créer"}
            </div>
            <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-none mb-3">
                Mon club
            </h1>
            {!initial && (
                <p className="text-zinc-400 mb-8">
                    Renseignez votre club. Une fois créé, il sera vérifié par un administrateur avant publication.
                </p>
            )}
            <form onSubmit={save} className="card-tu space-y-5">
                <Field label="Nom du club">
                    <input data-testid="club-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-tu" />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Sport">
                        <select data-testid="club-sport" value={f.sport} onChange={(e) => setF({ ...f, sport: e.target.value })} className="input-tu">
                            {SPORTS.map((s) => <option key={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Niveau">
                        <select value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} className="input-tu">
                            {LEVELS.map((l) => <option key={l}>{l}</option>)}
                        </select>
                    </Field>
                    <Field label="Département">
                        <input data-testid="club-department" required value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} className="input-tu" placeholder="Ex. 75 - Paris" />
                    </Field>
                    <Field label="Ville">
                        <input data-testid="club-city" required value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="input-tu" />
                    </Field>
                </div>

                <Field label="Catégories d'âge & effectifs">
                    <div className="flex flex-wrap gap-2 mb-4">
                        {AGE_CATS.map((c) => {
                            const active = !!f.teams_by_category.find((t) => t.age_category === c);
                            return (
                                <button
                                    type="button"
                                    key={c}
                                    data-testid={`age-${c}`}
                                    onClick={() => toggleAgeCategory(c)}
                                    className={[
                                        "px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors",
                                        active
                                            ? "border-orange-500 text-orange-500 bg-orange-500/10"
                                            : "border-zinc-800 text-zinc-300 hover:border-zinc-600",
                                    ].join(" ")}
                                >
                                    {c}
                                </button>
                            );
                        })}
                    </div>
                    {f.teams_by_category.length > 0 && (
                        <div className="space-y-2 border border-zinc-800 bg-zinc-950 p-4">
                            <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-bold pb-2 border-b border-zinc-800">
                                <div className="col-span-4">Catégorie</div>
                                <div className="col-span-4">Nb équipes</div>
                                <div className="col-span-4">Joueurs / équipe</div>
                            </div>
                            {f.teams_by_category.map((t) => (
                                <div key={t.age_category} className="grid grid-cols-12 gap-2 items-center">
                                    <div className="col-span-4 font-bold">{t.age_category}</div>
                                    <input
                                        data-testid={`team-count-${t.age_category}`}
                                        type="number"
                                        min={1}
                                        value={t.num_teams}
                                        onChange={(e) => updateTeam(t.age_category, "num_teams", e.target.value)}
                                        className="input-tu col-span-4 py-2"
                                    />
                                    <input
                                        data-testid={`players-per-team-${t.age_category}`}
                                        type="number"
                                        min={1}
                                        value={t.players_per_team}
                                        onChange={(e) => updateTeam(t.age_category, "players_per_team", e.target.value)}
                                        className="input-tu col-span-4 py-2"
                                    />
                                </div>
                            ))}
                            <div className="pt-3 mt-3 border-t border-zinc-800 flex items-center justify-between text-xs uppercase tracking-widest">
                                <span className="text-zinc-400">Total club</span>
                                <span className="text-orange-500 font-bold">
                                    {totalTeams} équipe{totalTeams > 1 ? "s" : ""} · {totalPlayers} joueurs
                                </span>
                            </div>
                        </div>
                    )}
                </Field>

                <Field label="Couleurs dominantes">
                    <div className="flex flex-wrap gap-2">
                        {COLORS.map((c) => (
                            <button
                                type="button"
                                key={c}
                                onClick={() => toggleColor(c)}
                                className={[
                                    "px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors",
                                    f.colors.includes(c)
                                        ? "border-orange-500 text-orange-500 bg-orange-500/10"
                                        : "border-zinc-800 text-zinc-300 hover:border-zinc-600",
                                ].join(" ")}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </Field>
                <Field label="Besoins (court)">
                    <input value={f.needs} onChange={(e) => setF({ ...f, needs: e.target.value })} className="input-tu" placeholder="Ex. maillots U13 et U15" />
                </Field>
                <Field label="Description">
                    <textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="input-tu" />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-zinc-800 pt-5">
                    <Field label="Nom du président">
                        <input
                            data-testid="club-president-name"
                            required
                            value={f.president_name}
                            onChange={(e) => setF({ ...f, president_name: e.target.value })}
                            className="input-tu"
                            placeholder="Prénom Nom"
                        />
                    </Field>
                    <Field label="Téléphone du président (10 chiffres)">
                        <input
                            data-testid="club-president-phone"
                            required
                            type="tel"
                            value={f.president_phone}
                            onChange={(e) => setF({ ...f, president_phone: e.target.value.replace(/[^0-9 .\-]/g, "") })}
                            placeholder="06 12 34 56 78"
                            className="input-tu"
                            inputMode="tel"
                            autoComplete="tel"
                        />
                    </Field>
                </div>
                <button data-testid="save-club" disabled={saving} className="btn-primary w-full">
                    {saving ? "Enregistrement…" : initial ? "Mettre à jour" : "Créer mon club"}
                </button>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="label-tu">{label}</label>
            {children}
        </div>
    );
}
